import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import { useCreateDataPointValues, useGetDataPointValues } from 'api/hooks/useDataPointValues';
import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { completedNavigatorStep } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { AuthContext } from 'contexts/Auth';
import useUnderwritingFunnelRemix from 'hooks/useUnderwritingFunnelRemix';
import { APPLICATION_FULL_PATH } from 'routes/constants';
import useSelector from 'store/useSelector';

import schema from './schema';
import { FormValues } from './types';
import { continueOnlineSubmit } from './utils/continueOnlineSubmit';

const INTERVIEW_URNS: Array<keyof FormValues> = [
  'urn:pg:data-point:insured-risk-activity-lived-or-traveled-overseas-past-two-years',
  'urn:pg:data-point:insured-risk-activity-lived-or-traveled-overseas-past-two-years-details',
  'urn:pg:data-point:insured-risk-activity-past-or-expect-travel-afghanistan-or-iraq',
  'urn:pg:data-point:insured-risk-activity-past-or-expect-travel-afghanistan-or-iraq-details',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-date-of-travel',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-destination',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-duration',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-reason',
];

const Travel: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lifeApplicationReferenceId = useSelector((app) => app.lifeApplicationReferenceId);
  const activeUserUuid = useSelector((app) => app.activeUserUuid);

  const { data, loading } = useGetDataPointValues({
    params: {
      urns: INTERVIEW_URNS,
    },
  });
  const { createDataPointValuesFromInterview } = useCreateDataPointValues();
  const isFunnelRemixActive = useUnderwritingFunnelRemix();

  if (loading) {
    return <GlobalLoader />;
  }

  const onSubmitComplete = async (values: FormValuesOutput<FormValues>) => {
    const res = await createDataPointValuesFromInterview(values, schema);

    if (res.error?.message) {
      return;
    }
    completedNavigatorStep(StepName.TRAVEL, activeUserUuid);

    isFunnelRemixActive
      ? navigate(APPLICATION_FULL_PATH)
      : await continueOnlineSubmit({
          lifeApplicationReferenceId,
          auth,
          navigate,
          dispatch,
        });
  };

  return (
    <NavigatorContent>
      <DynamicFormWrapper title="Travel">
        <DynamicForm<FormValues>
          name="travel"
          schema={schema}
          initialValues={data?.dataPointValues}
          onSubmit={onSubmitComplete}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default Travel;
